<script setup>
import {computed, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRouter} from 'vue-router';
import {useOriginTypeStore} from '@/store/stores/originType';
import {useAppStore} from '@/store/stores/app';
import OriginType from '@/models/OriginType';
import LoaderComponent from '@/components/helpers/LoaderComponent.vue';

// Disposables
const {t} = useI18n();
const router = useRouter();
const originTypeStore = useOriginTypeStore();
const appStore = useAppStore();

// Data
let originTypes = ref(null);
let categories = ref([]);

// Data initialization
originTypeStore.fetchOriginTypes().then((result) => {
    originTypes.value = result;
    setCategories();
});

// Computed
let isLoading = computed(() => appStore.isLoading);

// Methods
const setCategories = () => {
    const ownSupply = originTypes.value.find(originType => originType.id === OriginType.OWN_SUPPLY());
    const buyIn = originTypes.value.find(originType => originType.id === OriginType.BUY_IN());

    categories.value = [
        {
            title: t('inboundItems'),
            accent: 'olive',
            pages: [
                {
                    title: t('ownSupply'),
                    icon: 'mdi-tractor',
                    navigate: () => router.push({
                        name: 'inboundItems.create',
                        params: {originTypeId: ownSupply.id},
                    }),
                },
                {
                    title: t('buyIn'),
                    icon: 'mdi-truck-delivery',
                    navigate: () => router.push({
                        name: 'inboundItems.create',
                        params: {originTypeId: buyIn.id},
                    }),
                },
                {
                    title: t('directPacked'),
                    icon: 'mdi-archive-marker',
                    navigate: () => router.push({name: 'inboundItems.create-direct-packed'}),
                },
            ],
        },
        {
            title: t('production'),
            accent: 'forest',
            pages: [
                {
                    title: t('production'),
                    icon: 'mdi-factory',
                    navigate: () => router.push({name: 'production.locations'}),
                },
                {
                    title: t('planning'),
                    icon: 'mdi-calendar',
                    navigate: () => router.push({name: 'production.planning'}),
                },
                {
                    title: t('locationBoard'),
                    icon: 'mdi-view-column',
                    navigate: () => router.push({name: 'production.location-board'}),
                },
                {
                    title: t('locationRegistration'),
                    icon: 'mdi-map-marker-plus',
                    navigate: () => router.push({name: 'production.location-registration'}),
                },
            ],
        },
        {
            title: t('stock'),
            accent: 'amber',
            pages: [
                {
                    title: t('deliver'),
                    icon: 'mdi-human-dolly',
                    navigate: () => router.push({name: 'deliver.deliver'}),
                },
                {
                    title: t('returnOutput'),
                    icon: 'mdi-arrow-u-left-top',
                    navigate: () => router.push({name: 'deliver.return'}),
                },
            ],
        },
        {
            title: t('fust'),
            accent: 'steel',
            pages: [
                {
                    title: t('planning'),
                    icon: 'mdi-calendar-clock',
                    navigate: () => router.push({name: 'purchaseOrders.planning'}),
                },
            ],
        },
    ];
}
</script>

<template>
    <v-container
        class="start-screen scrollable fill-height"
        fluid
    >
        <template v-if="!isLoading">
            <div class="start-screen__inner">
                <start-screen-category
                    v-for="category in categories"
                    :key="category.title"
                    :title="category.title"
                    :accent="category.accent"
                    :pages="category.pages"
                />
            </div>
        </template>

        <loader-component
            v-else
            class="mx-auto"
        />
    </v-container>
</template>
